import { useEffect } from "react";
import { toast } from "react-hot-toast";

/**
 * Enhanced error handler that detects and recovers from chunk load errors
 * that occur when the app is redeployed while users have the old version open.
 * Adapted for Vite environments.
 */
export default function ChunkErrorHandler() {
  useEffect(() => {
    const RELOAD_COOLDOWN = 5000; // 5 seconds
    const MAX_RETRIES = 2;
    const reloadKey = "chunk-reload-attempt";
    const retryKey = "chunk-retry-count";
    const TOAST_ID = "deployment-reload";
    
    // Flag to prevent multiple simultaneous error handling
    let isHandlingError = false;
    let errorHandlingTimeout: ReturnType<typeof setTimeout> | null = null;

    /**
     * Check if error is a chunk loading error
     */
    const isDeploymentError = (error: unknown, message?: string): boolean => {
      // Get message from various possible error formats
      let errorStr = "";
      if (error instanceof Error) {
        errorStr = error.message;
      } else if (error && typeof error === "object" && "message" in error) {
        errorStr = String((error as any).message);
      } else {
        errorStr = String(error || "");
      }

      const msgStr = message || errorStr;
      const combined = `${errorStr} ${msgStr}`.toLowerCase();

      // Check for common chunk/deployment error patterns
      const isChunkError =
        combined.includes("chunkloaderror") ||
        combined.includes("loading chunk") ||
        combined.includes("failed to fetch dynamically imported module") ||
        combined.includes("loading css chunk") ||
        combined.includes("error loading dynamically imported module") ||
        // "Unexpected token '<'" is a classic sign of a missing JS file returning index.html
        combined.includes("unexpected token '<'") ||
        combined.includes("unexpected token (atl)") ||
        // Check for 404 errors on script tags (passed via Event)
        (error &&
         typeof error === "object" &&
         "target" in error &&
         (error as any).target &&
         typeof (error as any).target === "object" &&
         "tagName" in (error as any).target &&
         ((error as any).target.tagName === "SCRIPT" || (error as any).target.tagName === "LINK") &&
         "src" in (error as any).target &&
         isDeploymentResource((error as any).target.src || (error as any).target.href));

      return !!isChunkError;
    };

    /**
     * Check if a failed resource is a Vite chunk file
     */
    const isDeploymentResource = (url: string): boolean => {
      if (!url) return false;
      // In Vite, assets are usually in /assets/ subdirectory and end in .js or .css
      // We also check for .js/css in general if they are relative or from the same origin
      const isSameOrigin = url.startsWith("/") || url.includes(window.location.host);
      const isAsset = /\.(js|css|mjs)$/.test(url);
      
      return (
        url.includes("/assets/") ||
        (isSameOrigin && isAsset) ||
        url.includes("vite/client")
      );
    };

    /**
     * Handle deployment error (chunk) with retry logic and user notification
     */
    const handleDeploymentErrorWithRetry = (source: string) => {
      // Prevent multiple simultaneous error handling
      if (isHandlingError) {
        return;
      }

      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      const retryCount = Number.parseInt(sessionStorage.getItem(retryKey) || "0", 10);

      // Check cooldown period to detect infinite reload loops
      if (lastReload && now - Number.parseInt(lastReload, 10) < RELOAD_COOLDOWN) {
        if (retryCount >= MAX_RETRIES) {
          console.error("Max retry attempts reached for chunk error. Manual refresh required.");
          // Dismiss any existing toasts first
          toast.dismiss(TOAST_ID);
          toast.error("New version detected but failed to load. Please refresh manually.");
          return;
        }
        // Increment retry count
        sessionStorage.setItem(retryKey, (retryCount + 1).toString());
      } else {
        // Reset retry count if enough time passed since last reload
        sessionStorage.setItem(retryKey, "0");
      }

      // Set flag to prevent duplicate handling
      isHandlingError = true;

      console.warn(`[ChunkErrorHandler] Deployment error detected (${source}). Reloading page to fetch latest version...`);

      // Dismiss any existing error toasts from other parts of the app
      toast.dismiss();
      
      // Show our notification
      toast.loading("Updating to latest version...", {
        duration: 5000,
        id: TOAST_ID,
      });

      // Small delay to let user see the notification and for current tasks to settle
      sessionStorage.setItem(reloadKey, Date.now().toString());
      errorHandlingTimeout = setTimeout(() => {
        window.location.reload();
      }, 1500); 
    };

    /**
     * Handle error events (script/style load failures)
     */
    const handleError = (event: ErrorEvent) => {
      if (isHandlingError) return;

      const target = event.target as HTMLElement;
      
      // Check if it's a failed resource load (script, link)
      if (target && (target.tagName === "SCRIPT" || target.tagName === "LINK")) {
        const src = (target as HTMLScriptElement).src || (target as HTMLLinkElement).href || "";
        if (src && isDeploymentResource(src)) {
          event.preventDefault();
          event.stopPropagation();
          handleDeploymentErrorWithRetry(`resource load: ${target.tagName}`);
          return;
        }
      }

      // Check error message/object
      if (isDeploymentError(event.error, event.message)) {
        event.preventDefault();
        event.stopPropagation();
        handleDeploymentErrorWithRetry("error event");
      }
    };

    /**
     * Handle unhandled promise rejections (common for dynamic imports)
     */
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isHandlingError) {
        // If we're already reloading, silence other rejections to avoid toast spam
        event.preventDefault();
        return;
      }

      const reason = event.reason;
      if (isDeploymentError(reason, reason?.message)) {
        event.preventDefault();
        event.stopPropagation();
        handleDeploymentErrorWithRetry("promise rejection (dynamic import)");
      }
    };

    /**
     * Intercept fetch requests to detect 404s on chunk files early
     */
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        let url = "";
        if (typeof args[0] === "string") {
          url = args[0];
        } else if (args[0] instanceof Request) {
          url = args[0].url;
        } else if (args[0] instanceof URL) {
          url = args[0].href;
        }
        
        const response = await originalFetch(...args);
        
        // Handle 404s on JS/CSS assets
        if (!response.ok && response.status === 404 && url && isDeploymentResource(url)) {
          handleDeploymentErrorWithRetry("fetch 404 on asset");
          // Re-throw to cancel the current module loading attempt
          throw new Error("Chunk loading failed: 404");
        }
        
        return response;
      } catch (error) {
        throw error;
      }
    };

    /**
     * MutationObserver to catch and suppress error toasts from other sources 
     * when they are related to chunk loading.
     */
    const observer = new MutationObserver((mutations) => {
      if (isHandlingError) return;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            // Common toast attributes
            const classAttr = element.getAttribute('class') || '';
            const isErrorToast = 
              element.dataset.type === 'error' ||
              element.getAttribute('aria-label')?.toLowerCase().includes('error') ||
              classAttr.toLowerCase().includes('error');
            
            if (isErrorToast) {
              const text = element.textContent || '';
              if (isDeploymentError(null, text)) {
                handleDeploymentErrorWithRetry("intercepted error toast");
                element.remove(); // Hide the redundant error toast
              }
            }
          }
        });
      });
    });
    
    observer.observe(document.body, { childList: true, subtree: true });

    // Expose a test function to the global scope for local testing
    (window as any).__simulateChunkError = () => {
      console.log("Simulating chunk load error...");
      handleDeploymentErrorWithRetry("manual simulation");
    };

    // Register listeners
    window.addEventListener("error", handleError, true);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      if (errorHandlingTimeout) clearTimeout(errorHandlingTimeout);
      observer.disconnect();
      window.removeEventListener("error", handleError, true);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.fetch = originalFetch;
      delete (window as any).__simulateChunkError;
    };
  }, []);

  return null;
}
